// Keep the original curved boundaries; simplify only to locate short end caps.
export function sideRailCandidates(contours) {
  const seen = new Set();
  const candidates = [0.001, 0.005, 0.012, 0.025].flatMap((scale) =>
    candidatesAtScale(contours, scale).filter((pair) => {
      const key = pair.rail0 + "|" + pair.rail1;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
  );
  // Full-width ends take precedence over a sharp-tip interpretation.
  return candidates.some((p) => !p.tapered)
    ? candidates.filter((p) => !p.tapered)
    : candidates;
}

function candidatesAtScale(contours, scale) {
  if (contours?.length !== 1) return [];
  const p = contours[0].map(([x, y]) => ({ x, y }));
  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  if (p.length > 1 && distance(p[0], p[p.length - 1]) < 1e-8) p.pop();
  if (p.length < 3 || p.length > 5000) return [];
  // A triangular section has a cap and two sides meeting at a shared tip.
  // The general cap search requires four distinct endpoints, so it cannot
  // propose rails for these real (sometimes very small) cut fragments.
  // Keep their exact boundaries and let the normal quality/containment checks
  // select a valid pairing instead of dropping the section or rounding its tip.
  if (p.length === 3) {
    return p.map((tip, i) => ({
      rail0: `M${p[(i + 1) % 3].x},${p[(i + 1) % 3].y} L${tip.x},${tip.y}`,
      rail1: `M${p[(i + 2) % 3].x},${p[(i + 2) % 3].y} L${tip.x},${tip.y}`,
      tapered: true,
    }));
  }
  const n = p.length;
  const extent =
    Math.max(...p.map((v) => v.x)) -
    Math.min(...p.map((v) => v.x)) +
    Math.max(...p.map((v) => v.y)) -
    Math.min(...p.map((v) => v.y));
  const tolerance = extent * scale;
  const segmentDistance = (q, a, b) => {
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const t = Math.max(
      0,
      Math.min(
        1,
        ((q.x - a.x) * dx + (q.y - a.y) * dy) / (dx * dx + dy * dy || 1)
      )
    );
    return Math.hypot(q.x - a.x - t * dx, q.y - a.y - t * dy);
  };
  const arc = (a, b) => {
    const out = [a];
    while (a !== b) {
      a = (a + 1) % n;
      out.push(a);
    }
    return out;
  };
  const simplify = (ids) => {
    let best = tolerance,
      index = -1;
    for (let i = 1; i < ids.length - 1; i++) {
      const d = segmentDistance(p[ids[i]], p[ids[0]], p[ids[ids.length - 1]]);
      if (d > best) {
        best = d;
        index = i;
      }
    }
    return index < 0
      ? [ids[0], ids[ids.length - 1]]
      : [
          ...simplify(ids.slice(0, index + 1)).slice(0, -1),
          ...simplify(ids.slice(index)),
        ];
  };
  let far = 1;
  for (let i = 2; i < n; i++)
    if (distance(p[0], p[i]) > distance(p[0], p[far])) far = i;
  const vertices = [
    ...simplify(arc(0, far)).slice(0, -1),
    ...simplify(arc(far, 0)).slice(0, -1),
  ];
  const turn = (i) => {
    const a = p[vertices[(i + vertices.length - 1) % vertices.length]],
      b = p[vertices[i]],
      c = p[vertices[(i + 1) % vertices.length]];
    return Math.acos(
      Math.max(
        -1,
        Math.min(
          1,
          ((b.x - a.x) * (c.x - b.x) + (b.y - a.y) * (c.y - b.y)) /
            (distance(a, b) * distance(b, c) || 1)
        )
      )
    );
  };
  const length = (ids) =>
    ids.slice(1).reduce((s, id, i) => s + distance(p[ids[i]], p[id]), 0);
  const caps = [];
  for (let i = 0; i < vertices.length; i++) {
    for (let step = 1; step <= Math.min(3, vertices.length - 3); step++) {
      const j = (i + step) % vertices.length;
      if (turn(i) < 0.55 || turn(j) < 0.55) continue;
      const a = vertices[i],
        b = vertices[j],
        len = distance(p[a], p[b]);
      if (len > tolerance && length(arc(a, b)) <= len * 1.08)
        caps.push({ a, b, len });
    }
  }
  const d = (ids) => "M" + ids.map((i) => `${p[i].x},${p[i].y}`).join(" L");
  const result = [];
  for (let i = 0; i < caps.length; i++)
    for (let j = i + 1; j < caps.length; j++) {
      const a = caps[i],
        b = caps[j];
      // A tiny bevel at a pointed tip is not a full-width end cap. Pairing
      // it with the opposite end wraps a rail back along the actual cap.
      // Strongly tapered tips retain the validated two-point fallback.
      if (Math.min(a.len, b.len) < Math.max(a.len, b.len) * 0.15) continue;
      if (new Set([a.a, a.b, b.a, b.b]).size < 4) continue;
      const capA = new Set(arc(a.a, a.b));
      if (arc(b.a, b.b).some((id) => capA.has(id))) continue;
      const left = arc(a.b, b.a),
        right = arc(b.b, a.a);
      // Oblique junction cuts can be longer than the shorter side of a
      // trapezoid. Compare the pair as a whole, rather than requiring both
      // individual sides to be longer than either end.
      const leftLength = length(left),
        rightLength = length(right);
      if (
        Math.min(leftLength, rightLength) < Math.max(a.len, b.len) * 0.4 ||
        leftLength + rightLength < (a.len + b.len) * 1.1
      )
        continue;
      result.push({ rail0: d(left), rail1: d(right.reverse()) });
    }
  for (const cap of caps) {
    const capPoints = new Set(arc(cap.a, cap.b));
    for (let i = 0; i < vertices.length; i++) {
      const tip = vertices[i];
      if (capPoints.has(tip) || turn(i) < 2.45) continue;
      const left = arc(cap.b, tip),
        right = arc(tip, cap.a).reverse();
      if (Math.min(length(left), length(right)) < cap.len * 1.15) continue;
      result.push({ rail0: d(left), rail1: d(right), tapered: true });
    }
  }
  return result;
}
